import { usePropertiesQuery } from "@/common/api/services/properties";
import { useZippopotamQuery } from "@/common/api/third-party-services/zippopotam";
import { zippopotamService } from "@/common/api/third-party-services/zippopotam";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { PropertyType } from "@/modules/Properties/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, MapPin, X } from "lucide-react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface CreatePropertyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const propertyTypes = [
  "apartment",
  "house",
  "office",
  "commercial",
  "land",
] as const;
type PropertyTypeValues = (typeof propertyTypes)[number];

const CreatePropertyDialog = ({
  open,
  onOpenChange,
}: CreatePropertyDialogProps) => {
  const { t } = useTranslation();
  const [postalCode, setPostalCode] = useState<string>("");
  const [locationInfo, setLocationInfo] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [applyLocationInfo, setApplyLocationInfo] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const createPropertySchema = z.object({
    name: z.string().min(1, t("properties.form.validation.nameRequired")),
    type: z.enum(propertyTypes, {
      required_error: t("properties.form.validation.typeRequired"),
    }),
    status: z.enum(
      ["available", "occupied", "sold", "pending", "maintenance"],
      {
        required_error: t("properties.form.validation.statusRequired"),
      },
    ),
    price: z
      .number({
        required_error: t("properties.form.validation.priceRequired"),
        invalid_type_error: t("properties.form.validation.priceRequired"),
      })
      .min(0, t("properties.form.validation.priceMin")),
    currency: z.literal("USD"),
    postalCode: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    stateAbbreviation: z.string().optional(),
    country: z.string().optional(),
    countryAbbreviation: z.string().optional(),
    geoLocation: z.any().optional(),
  });

  type CreatePropertyFormValues = z.infer<typeof createPropertySchema>;

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { createPropertyMutation } = usePropertiesQuery();
  const zippopotamQuery = useZippopotamQuery();

  const form = useForm<CreatePropertyFormValues>({
    resolver: zodResolver(createPropertySchema),
    defaultValues: {
      currency: "USD",
      status: "available",
      type: "apartment",
      postalCode: "",
      city: "",
      state: "",
      stateAbbreviation: "",
      country: "",
      countryAbbreviation: "",
    },
  });

  const { mutate: createProperty, isPending } = useMutation({
    ...createPropertyMutation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      onOpenChange(false);
      form.reset();
      toast({
        title: t("properties.toast.createSuccess.title"),
        description: t("properties.toast.createSuccess.description"),
      });
    },
    onError: (error) => {
      console.error("Failed to create property:", error);
      toast({
        variant: "destructive",
        title: t("properties.toast.createError.title"),
        description: t("properties.toast.createError.description"),
      });
    },
  });

  const handleCancelSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsSearching(false);
      toast({
        title: t("properties.location.searchCancelled.title"),
        description: t("properties.location.searchCancelled.description"),
      });
    }
  };

  const handleSearchLocation = () => {
    if (!postalCode) {
      toast({
        variant: "destructive",
        title: t("properties.location.searchError.title"),
        description: t("properties.location.searchError.missingFields"),
      });
      return;
    }

    // Якщо попередній запит все ще виконується, скасовуємо його
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Створюємо новий AbortController для поточного запиту
    abortControllerRef.current = new AbortController();

    setIsSearching(true);
    setLocationInfo(null);
    setApplyLocationInfo(false);

    queryClient
      .fetchQuery({
        queryKey: zippopotamQuery.locationByPostalCodeQuery("US", postalCode).queryKey,
        queryFn: () => zippopotamService.getLocationByPostalCode(
          "US",
          postalCode,
          abortControllerRef.current?.signal
        ),
      })
      .then((data) => {
        setLocationInfo(data);
        form.setValue("postalCode", postalCode);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error("Failed to fetch location data:", error);
          toast({
            variant: "destructive",
            title: t("properties.location.searchError.title"),
            description: t("properties.location.searchError.notFound"),
          });
        }
      })
      .finally(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current = null;
          setIsSearching(false);
        }
      });
  };

  const handleApplyLocationInfo = () => {
    if (locationInfo && locationInfo.places && locationInfo.places.length > 0) {
      const place = locationInfo.places[0];

      form.setValue("city", place["place name"]);
      form.setValue("state", place.state);
      form.setValue("stateAbbreviation", place["state abbreviation"]);
      form.setValue("country", locationInfo.country);
      form.setValue(
        "countryAbbreviation",
        locationInfo["country abbreviation"],
      );

      // Створюємо GeoJSON Point об'єкт з координатами
      if (place.longitude && place.latitude) {
        const geoLocation = {
          type: "Point",
          coordinates: [
            parseFloat(place.longitude),
            parseFloat(place.latitude),
          ],
        };
        form.setValue("geoLocation", geoLocation);
      }

      setApplyLocationInfo(true);

      toast({
        title: t("properties.location.applySuccess.title"),
        description: t("properties.location.applySuccess.description"),
      });
    }
  };

  const handleSubmit = (values: CreatePropertyFormValues) => {
    createProperty(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh]">
        <DialogHeader className="px-1 pb-2">
          <DialogTitle>{t("properties.dialog.create.title")}</DialogTitle>
          <DialogDescription>
            {t("properties.dialog.create.description")}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-full max-h-[calc(80vh-6rem)] pr-4">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-5"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("properties.form.name")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t("properties.form.namePlaceholder")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("properties.form.type")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t("properties.form.typePlaceholder")}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {propertyTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {t(`properties.type.${type}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("properties.form.price")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder={t("properties.form.pricePlaceholder")}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Блок пошуку місцезнаходження за поштовим індексом */}
              <Card className="border">
                <CardContent className="space-y-4 pt-4">
                  <h3 className="font-medium text-sm">
                    {t("properties.location.title")}
                  </h3>

                  <div>
                    <FormLabel>{t("properties.location.postalCode")}</FormLabel>
                    <div className="flex gap-2">
                      <Input
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        placeholder={t("properties.location.postalCodePlaceholder")}
                      />
                      {isSearching ? (
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={handleCancelSearch}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={handleSearchLocation}
                        >
                          <MapPin className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {isSearching && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-sm text-muted-foreground">
                        {t("properties.location.searching")}
                      </span>
                    </div>
                  )}

                  {locationInfo &&
                    locationInfo.places &&
                    locationInfo.places.length > 0 && (
                      <div className="mt-2 space-y-3">
                        <div className="flex justify-between items-center">
                          <h4 className="font-medium text-sm">
                            {t("properties.location.foundInfo")}
                          </h4>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleApplyLocationInfo}
                            disabled={applyLocationInfo}
                          >
                            {applyLocationInfo ? (
                              <>
                                <Check className="h-4 w-4 mr-1" />
                                {t("properties.location.applied")}
                              </>
                            ) : (
                              t("properties.location.apply")
                            )}
                          </Button>
                        </div>
                        <div className="text-sm space-y-1.5 text-muted-foreground">
                          <p>
                            <span className="font-medium text-foreground">
                              {t("properties.location.city")}:
                            </span>{" "}
                            {locationInfo.places[0]["place name"]}
                          </p>
                          <p>
                            <span className="font-medium text-foreground">
                              {t("properties.location.state")}:
                            </span>{" "}
                            {locationInfo.places[0].state} (
                            {locationInfo.places[0]["state abbreviation"]})
                          </p>
                          <p>
                            <span className="font-medium text-foreground">
                              {t("properties.location.country")}:
                            </span>{" "}
                            {locationInfo.country} (
                            {locationInfo["country abbreviation"]})
                          </p>
                          {locationInfo.places[0].latitude &&
                            locationInfo.places[0].longitude && (
                              <p>
                                <span className="font-medium text-foreground">
                                  {t("properties.location.coordinates")}:
                                </span>{" "}
                                {locationInfo.places[0].latitude},{" "}
                                {locationInfo.places[0].longitude}
                              </p>
                            )}
                        </div>
                      </div>
                    )}
                </CardContent>
              </Card>

              {applyLocationInfo && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("properties.form.city")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("properties.form.state")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("properties.form.country")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="postalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("properties.form.postalCode")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <DialogFooter className="pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending
                    ? t("common.saving")
                    : t("properties.dialog.create.submit")}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePropertyDialog;
